import { useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, Loader2, Upload, UploadCloud } from 'lucide-react';
import { uploadMedia } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { TakeoverEditor, type TakeoverEditorHandle, type TakeoverValue } from './TakeoverEditor';
import { TAKEOVER_TEMPLATES, type TakeoverFill, type TakeoverFormat } from './takeovers';
import { clampInFrame, coverTransform, exportExt, type ExportFormat } from './photoSlot';
import { FormatToggle } from './FormatToggle';

const CARD_MEDIA_CATEGORY = 'social_card' as const;
const slugify = (s: string) => (s || 'takeover').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export interface TakeoverComposerProps {
  accountSlug?: string;
  className?: string;
}

export function TakeoverComposer({ accountSlug, className }: TakeoverComposerProps) {
  const [format, setFormat] = useState<TakeoverFormat>('ig-post');
  const template = useMemo(
    () => TAKEOVER_TEMPLATES.find((t) => t.format === format) ?? TAKEOVER_TEMPLATES[0],
    [format],
  );
  const [fill, setFill] = useState<TakeoverFill>('navy');
  const [value, setValue] = useState<TakeoverValue>({ photoSrc: null, transform: null, name: '', date: '', time: '' });
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('jpeg');

  const editorRef = useRef<TakeoverEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearSaved = () => {
    setUploadedUrl(null);
    setUploadError(null);
    setCopied(false);
  };
  const update = (patch: Partial<TakeoverValue>) => {
    clearSaved();
    setValue((v) => ({ ...v, ...patch }));
  };

  const changeFormat = (f: TakeoverFormat) => {
    clearSaved();
    setFormat(f);
    setValue((v) => ({ ...v, transform: null })); // different canvas → re-fit
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    clearSaved();
    const url = URL.createObjectURL(file);
    const probe = new Image();
    probe.onload = () => setDims({ w: probe.naturalWidth, h: probe.naturalHeight });
    probe.src = url;
    setValue((v) => ({ ...v, photoSrc: url, transform: null }));
  };

  const zoomTo = (nextScale: number) => {
    if (!value.transform || !dims) return;
    const frame = template.photoFrame;
    const cx = frame.x + frame.width / 2;
    const cy = frame.y + frame.height / 2;
    const tf = value.transform;
    const imgPtX = (cx - tf.x) / tf.scale;
    const imgPtY = (cy - tf.y) / tf.scale;
    const clamped = clampInFrame(cx - imgPtX * nextScale, cy - imgPtY * nextScale, dims.w, dims.h, nextScale, frame);
    update({ transform: { scale: nextScale, ...clamped } });
  };

  const handleDownload = () => {
    const url = editorRef.current?.toDataURL(exportFormat);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(value.name)}-takeover-${template.format}.${exportExt(exportFormat)}`;
    a.click();
  };

  const handleUpload = async () => {
    const blob = await editorRef.current?.toBlob(exportFormat);
    if (!blob) return setUploadError('Could not render. Try again.');
    setUploading(true);
    setUploadError(null);
    try {
      const base = `${slugify(value.name)}-takeover-${template.format}`;
      const result = await uploadMedia(blob, { category: CARD_MEDIA_CATEGORY, identifier: base, fileName: `${base}.${exportExt(exportFormat)}`, accountSlug });
      setUploadedUrl(result.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!uploadedUrl) return;
    try {
      await navigator.clipboard.writeText(uploadedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
  const cover = dims ? coverTransform(dims.w, dims.h, template.photoFrame).scale : 1;
  // Allow shrinking well below "cover" (down to ~1/3 of fit-inside) so logos can
  // sit small on the background; zoom in up to 4× cover for photos.
  const contain = dims ? Math.min(template.photoFrame.width / dims.w, template.photoFrame.height / dims.h) : 1;
  const zoomMin = contain * 0.35;

  return (
    <div className={className}>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex justify-center">
          <div className="w-full max-w-[440px]">
            <TakeoverEditor ref={editorRef} template={template} value={value} fill={fill} onChange={setValue} />
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Format</label>
            <select value={format} onChange={(e) => changeFormat(e.target.value as TakeoverFormat)} className={inputClass}>
              {TAKEOVER_TEMPLATES.map((t) => (
                <option key={t.id} value={t.format}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Container background</label>
            <div className="inline-flex rounded-md border border-input p-0.5">
              {(['navy', 'black'] as TakeoverFill[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    clearSaved();
                    setFill(f);
                  }}
                  className={`rounded px-3 py-1 text-sm capitalize transition-colors ${
                    fill === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f === 'navy' ? 'Blue' : 'Black'}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">Black works well with transparent logos/photos.</p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Photo (full-bleed)</label>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            <Button type="button" variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" />
              {value.photoSrc ? 'Replace photo' : 'Upload photo'}
            </Button>
            {value.photoSrc && value.transform && dims && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Zoom</span>
                <input
                  type="range"
                  min={zoomMin}
                  max={cover * 4}
                  step={0.01}
                  value={value.transform.scale}
                  onChange={(e) => zoomTo(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => update({ transform: coverTransform(dims.w, dims.h, template.photoFrame) })}
                >
                  Reset
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Artist name</label>
            <input type="text" value={value.name} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Etape" className={inputClass} />
          </div>

          {template.date && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Date</label>
              <input type="text" value={value.date ?? ''} onChange={(e) => update({ date: e.target.value })} placeholder="e.g. 4 MAR 2026" className={inputClass} />
            </div>
          )}
          {template.time && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Time</label>
              <input type="text" value={value.time ?? ''} onChange={(e) => update({ time: e.target.value })} placeholder="e.g. 18:45 - 22:15" className={inputClass} />
            </div>
          )}

          <div className="space-y-2 border-t pt-5">
            <FormatToggle value={exportFormat} onChange={setExportFormat} />
            <Button type="button" variant="outline" className="w-full" onClick={handleDownload} disabled={!value.photoSrc}>
              <Download className="mr-2 h-4 w-4" />
              Download {exportFormat === 'jpeg' ? 'JPG' : 'PNG'}
            </Button>
            <Button type="button" className="w-full" onClick={handleUpload} disabled={!value.photoSrc || uploading}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
              {uploading ? 'Saving…' : 'Save to storage'}
            </Button>
            {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
            {uploadedUrl && (
              <div className="rounded-md border border-input bg-muted/40 p-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Saved to storage</p>
                <div className="flex items-center gap-2">
                  <a href={uploadedUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-xs text-primary hover:underline" title={uploadedUrl}>
                    {uploadedUrl}
                  </a>
                  <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
